import {
  LogoThumb,
  OneMindCall,
  OneMindInProduct,
} from '../components/illustrations'
import type { CaseStudy } from './types'

export const oneMind: CaseStudy = {
  id: 'OneMind',
  card: {
    id: 'OneMind',
    href: '/1Mind',
    image: '/assets/case-study/1mind/01.webp',
    imageAlt: '1Mind',
    title: '1Mind',
    domain: '1mind.com',
    miniDescription:
      'AI "Superhuman" that qualifies, demos, and closes - on site and on live calls.',
    caseNum: '03 - AI - Enterprise sales',
    lead: 'An AI "Superhuman" sales agent with a face and a voice - qualifying buyers, running live demos, and handling objections across the website, inside the product, and on live Zoom calls.',
    tags: [
      { label: 'Real-time voice + avatar', key: true },
      { label: 'OpenAI + Gemini' },
      { label: 'Deterministic hybrid', key: true },
      { label: 'WebRTC / Zoom' },
      { label: 'CRM sync' },
    ],
    outcomes: [
      'One agent doing the work of 83 SDRs + 19 sales engineers',
      'Hybrid LLM + deterministic layer to keep facts honest',
      'Two-way Salesforce / HubSpot / Slack sync, fully logged',
    ],
    thumb: (
      <LogoThumb
        src="/assets/case-study/1mind/1mind.svg"
        alt="1Mind"
        color="#a85fe0"
      />
    ),
  },
  imageSizes: {
    '/assets/case-study/1mind/01.webp': { width: 2137, height: 1327 },
    '/assets/case-study/1mind/02.webp': { width: 2136, height: 1324 },
  },
  content: {
    metaTitle: '1Mind — Case Study · ThroughPilot',
    metaDescription:
      'How ThroughPilot embedded with 1Mind to harden the real-time AI sales agent: voice, avatar, deterministic guardrails, and CRM sync.',
    path: '/1Mind',
    kicker: 'Case study · AI · Enterprise sales',
    title: (
      <>
        1<em>Mind.com</em>
      </>
    ),
    liveHref: 'https://www.1mind.com',
    lede: 'An AI “Superhuman” that sells — a photoreal, voice-driven agent that qualifies buyers, runs live demos, and handles objections across the website, inside the product, and on the call itself.',
    meta: [
      { label: 'Role', value: 'Senior Product Engineering — Embedded' },
      {
        label: 'Status',
        value: (
          <a
            href="https://www.1mind.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            Live · 1mind.com ↗
          </a>
        ),
      },
      { label: 'Service', value: 'Embed — Staff augmentation' },
    ],
    chips: [
      { label: 'Real-time voice + avatar', key: true },
      { label: 'OpenAI + Google Gemini' },
      { label: 'Deterministic + LLM hybrid', key: true },
      { label: 'WebRTC / Zoom SDK' },
      { label: 'Salesforce / HubSpot', key: true },
      { label: 'Clari + Salesloft' },
      { label: 'ISO 27001 / 42001' },
    ],
    heroImage: {
      src: '/assets/case-study/1mind/01.webp',
      alt: '1Mind Superhuman — photoreal AI sales agent on the website',
    },
    overview: {
      label: 'The brief',
      title: (
        <>
          A salesperson that <em>never sleeps.</em>
        </>
      ),
      body: (
        <>
          <p>
            Inbound moves faster than any sales team can. A buyer lands on the
            site at 2am, has three questions, and is gone before an SDR ever sees
            the lead. 1Mind set out to close that gap with a
            &ldquo;Superhuman&rdquo; — an AI agent with a face, a voice, and real
            product knowledge that can actually <i>have the conversation.</i>
          </p>
          <p>
            Each Superhuman is built for one company&rsquo;s product, market, and
            buyer personas. It qualifies prospects in natural conversation, gives
            a tailored demo, answers objections with competitive and product
            context, and books the meeting — then writes everything back to the
            CRM more reliably than most reps.
          </p>
          <p>
            We embedded with the team to harden the real-time engine, the model
            orchestration, and the integrations — the production layer that has to
            be fast, accurate, and trustworthy at enterprise scale.
          </p>
        </>
      ),
    },
    highlights: {
      title: 'What makes it work.',
      features: [
        {
          num: '01 — Superhuman on the site',
          title: 'It meets buyers the moment they land.',
          media: {
            src: '/assets/case-study/1mind/02.webp',
            alt: '1Mind Superhuman greeting a visitor on the website in real time',
          },
          body: (
            <p>
              A conversational agent lives on the homepage and intercepts intent
              in real time — voice or text. It reads the page the visitor is on,
              qualifies them, runs a tailored walkthrough, and books a meeting
              without a form or a wait.
            </p>
          ),
          points: [
            'Photoreal avatar with sub-second voice response',
            'Qualifies, demos, and books — all in one session',
            'Picks up product and competitive context per account',
          ],
        },
        {
          num: '02 — In-product guide',
          title: 'The right nudge before the handoff.',
          flip: true,
          media: <OneMindInProduct />,
          body: (
            <p>
              For product-led teams, the Superhuman rides along inside the app —
              guiding trial users through activation, answering &ldquo;how do
              I&hellip;&rdquo; in context, and intercepting the ones worth a human
              conversation before they ever reach a rep.
            </p>
          ),
          points: [
            'Activation guidance scoped to where the user actually is',
            'Flags high-intent trials for the AE team',
            'Cuts the drop-off between sign-up and first value',
          ],
        },
        {
          num: '03 — Live on the call',
          title: 'The sales engineer who already knows the stack.',
          media: <OneMindCall />,
          body: (
            <p>
              The Superhuman joins live Zoom and video calls as a participant —
              sitting beside the account executive as the sales engineer, fielding
              deep technical questions and demoing on the fly, so one rep can run
              calls that used to need a whole pre-sales team.
            </p>
          ),
          points: [
            'Joins video calls as a real-time participant',
            'Answers technical and pricing questions instantly',
            'Frees the AE to own the relationship, not the trivia',
          ],
        },
        {
          num: '04 — Honest by design',
          title: 'Generative where it helps, deterministic where it counts.',
          flip: true,
          media: (
            <div className="acard">
              <div className="acard-head">
                <div className="ring">
                  <span>✓</span>
                </div>
                <div className="ht">
                  <div className="b">Conversation logged</div>
                  <div className="m">CRM · Salesforce — synced</div>
                  <div className="s">Hallucination guardrails on</div>
                </div>
              </div>
              <div className="arow">
                <div className="al">Pushed to CRM</div>
                <div className="kw">
                  <span className="miss">Stage → Qualified</span>
                  <span className="miss">Budget captured</span>
                  <span className="miss">Next step booked</span>
                </div>
              </div>
              <div className="arow">
                <div className="al">Posted to Slack</div>
                <ul className="fixes">
                  <li>High-intent buyer engaged from pricing page</li>
                  <li>Asked about SSO &amp; SOC 2 — flagged for AE</li>
                  <li>Demo booked for Thursday 10:00</li>
                </ul>
              </div>
            </div>
          ),
          body: (
            <p>
              1Mind pairs frontier LLMs with a deterministic layer, so the parts
              that must be exact — pricing, product facts, qualification logic —
              never get &ldquo;interpreted.&rdquo; Every conversation is logged,
              written back to the CRM, and surfaced in Slack for humans to
              inspect.
            </p>
          ),
          points: [
            'Hybrid OpenAI + Gemini with deterministic guardrails',
            'Reliable two-way CRM & Slack sync',
            'Full transcript trail — nothing happens off the record',
          ],
        },
      ],
    },
    hood: {
      title: 'Real-time, and built to be trusted.',
      cards: [
        {
          t: 'Voice + avatar',
          v: 'Real-time pipeline',
          d: 'Streaming speech, lip-synced avatar, sub-second turn-taking.',
        },
        {
          t: 'Reasoning',
          v: 'OpenAI + Gemini',
          d: 'Frontier LLMs for conversation, demos, and objection handling.',
        },
        {
          t: 'Accuracy',
          v: 'Deterministic layer',
          d: (
            <>
              Hard rules for pricing &amp; facts — cuts hallucination at the
              source.
            </>
          ),
        },
        {
          t: 'Calls',
          v: 'WebRTC / Zoom SDK',
          d: 'Joins live video as a first-class participant.',
        },
        {
          t: 'Revenue stack',
          v: 'CRM + Clari/Salesloft',
          d: (
            <>
              Two-way sync into Salesforce, HubSpot, cadences &amp; forecasting.
            </>
          ),
        },
        {
          t: 'Trust',
          v: 'ISO 27001 / 42001',
          d: 'Certified, with customer data excluded from model training.',
        },
      ],
      note: (
        <>
          The hard part wasn&rsquo;t making it talk — it was making it{' '}
          <b>
            fast enough to feel human and accurate enough to trust with a deal.
          </b>{' '}
          Streaming voice has no room for lag, and a sales agent that invents a
          price is worse than no agent at all. The deterministic layer guarantees
          the facts; the LLMs handle the conversation around them.
        </>
      ),
    },
    outcome: {
      label: 'Outcome',
      title: <>One agent, a whole team&rsquo;s output.</>,
      metrics: [
        {
          n: (
            <>
              83<em>+19</em>
            </>
          ),
          l: 'SDR & SE capacity, in one Superhuman',
        },
        {
          n: (
            <>
              88<em>%</em>
            </>
          ),
          l: 'More buyer engagement',
        },
        {
          n: (
            <>
              78<em>%</em>
            </>
          ),
          l: 'More free trials started',
        },
        {
          n: (
            <>
              25<em>%</em>
            </>
          ),
          l: 'Increase in revenue',
        },
      ],
    },
    prev: {
      href: '/FairyHomes',
      dir: '← Prev case',
      ttl: (
        <>
          <em>FairyHomes</em>.ae
        </>
      ),
    },
    next: {
      href: '/Peaque',
      dir: 'Next case →',
      ttl: (
        <>
          <em>Peaque.io</em>
        </>
      ),
    },
  },
}
